import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  type Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import EventsCalendar from "../components/EventsCalendar";
import EventsPageEventDetails from "../components/EventsPageEventDetails";
import EventInsights from "../components/insights/EventInsights";
import {
  ADMIN_BULK_UPLOAD_EVENTS,
  ADMIN_VALIDATE_BULK_UPLOAD_EVENTS,
} from "../gql/mutations/event";
import { DEFAULT_OFFSET, ROWS_PER_PAGE_DEFAULT, ROWS_PER_PAGE_OPTIONS } from "../helpers/const";
import { usePagination } from "../helpers/hooks";
import { Spacing } from "../types/enum";
import { useAdminSearchEventsQuery } from "../types/graphql";

interface BulkUploadEventError {
  row: number;
  eventTitle?: string;
  column: string;
  message: string;
}

interface BulkUploadCreatedEvent {
  row: number;
  id: string;
  title?: string;
}

interface BulkUploadInviteWarning {
  row: number;
  eventTitle?: string;
  column: string;
  message: string;
}

interface BulkUploadValidationResult {
  adminValidateBulkUploadEvents: {
    totalRows: number;
    validationErrors: BulkUploadEventError[];
    inviteWarnings: BulkUploadInviteWarning[];
  };
}

interface BulkUploadResult {
  adminBulkUploadEvents: {
    createdCount: number;
    createdEvents: BulkUploadCreatedEvent[];
    validationErrors: BulkUploadEventError[];
    inviteWarnings: BulkUploadInviteWarning[];
  };
}

/**
 * Styles
 */
const useStyles = makeStyles()((_theme: Theme) => ({
  search: {
    width: "50%",
  },
}));

const EventsPage: React.FC = () => {
  const { classes } = useStyles();

  const [view, setView] = useState<"calendar" | "list" | "insights">("calendar");
  const [query, setQuery] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [offset, setOffset] = useState<number>(DEFAULT_OFFSET);
  const [limit, setLimit] = useState<number>(ROWS_PER_PAGE_DEFAULT);

  const { data, loading, error, refetch } = useAdminSearchEventsQuery({
    fetchPolicy: "network-only",
    variables: {
      filters: {
        title: searchValue,
        limit,
        offset,
      },
    },
  });

  const { page, rowsPerPage, items, onChangePage, onChangeRowsPerPage } = usePagination(
    data?.adminSearchEvents?.items ?? [],
  );

  useEffect(() => {
    setOffset(page * rowsPerPage);
  }, [page, rowsPerPage]);

  useEffect(() => {
    setLimit(rowsPerPage);
  }, [rowsPerPage]);

  const debounceCallback = useCallback(
    debounce((searchVal: string) => {
      setSearchValue(searchVal);
    }, 1000),
    [],
  );

  const handleChange = (event: any) => {
    setQuery(event.target.value);
    debounceCallback(event.target.value);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [validationResult, setValidationResult] = useState<
    BulkUploadValidationResult["adminValidateBulkUploadEvents"] | null
  >(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validatedFile, setValidatedFile] = useState<File | null>(null);

  const [uploadResult, setUploadResult] = useState<
    BulkUploadResult["adminBulkUploadEvents"] | null
  >(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);

  const [validateMutation] = useMutation<BulkUploadValidationResult>(
    ADMIN_VALIDATE_BULK_UPLOAD_EVENTS,
  );
  const [bulkUploadMutation] = useMutation<BulkUploadResult>(ADMIN_BULK_UPLOAD_EVENTS);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateFile = async (file: File) => {
    setUploading(true);
    setValidationResult(null);
    setUploadError(null);

    try {
      const { data: result } = await validateMutation({
        variables: { input: { file } },
      });

      if (result) {
        setValidationResult(result.adminValidateBulkUploadEvents);
        setValidatedFile(file);
      }
      setValidationDialogOpen(true);
    } catch (err: any) {
      setUploadError(err?.message ?? "Validation failed");
      setValidationDialogOpen(true);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleProceedWithUpload = async () => {
    if (!validatedFile) return;
    setValidationDialogOpen(false);
    setUploading(true);
    setUploadResult(null);
    setUploadError(null);

    try {
      const { data: result } = await bulkUploadMutation({
        variables: { input: { file: validatedFile } },
      });

      if (result) {
        setUploadResult(result.adminBulkUploadEvents);
      }
      setResultsDialogOpen(true);
      void refetch();
    } catch (err: any) {
      setUploadError(err?.message ?? "Upload failed");
      setResultsDialogOpen(true);
    } finally {
      setUploading(false);
      setValidatedFile(null);
      setValidationResult(null);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await validateFile(file);
  };

  const handleCloseValidationDialog = () => {
    setValidationDialogOpen(false);
    setValidationResult(null);
    setValidatedFile(null);
    setUploadError(null);
  };

  const handleCloseResultsDialog = () => {
    setResultsDialogOpen(false);
    setUploadResult(null);
    setUploadError(null);
  };

  const allErrors: BulkUploadEventError[] = [
    ...(validationResult?.validationErrors ?? []),
    ...(validationResult?.inviteWarnings ?? []),
  ];
  const hasErrors = allErrors.length > 0;

  return (
    <>
      <Box
        mb={Spacing.m}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        <Typography variant="h3" color="textPrimary">
          Events
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
          >
            <ToggleButton value="calendar">Calendar</ToggleButton>
            <ToggleButton value="list">List</ToggleButton>
            <ToggleButton value="insights">Insights</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUploadClick}
            disabled={uploading}
            loading={uploading}
          >
            Upload Events
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </Box>
      </Box>

      {view === "calendar" && <EventsCalendar />}

      {view === "insights" && <EventInsights />}

      {view === "list" && (
        <>
          <Box mb={Spacing.m} className={classes.search}>
            <TextField
              fullWidth
              label="Search event"
              variant="outlined"
              value={query}
              onChange={handleChange}
            />
          </Box>
          {error ? (
            <Typography color="error">An error happened while loading events</Typography>
          ) : (
            <Card>
              {loading ? (
                <Box display="flex" justifyContent="center" p={Spacing.xxl}>
                  <CircularProgress />
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox />
                      </TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Organizer</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Start time</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(items ?? []).map((event) => (
                      <EventsPageEventDetails key={event.id} event={event} />
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        page={page}
                        count={data?.adminSearchEvents?.total ?? 0}
                        onPageChange={onChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={onChangeRowsPerPage}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              )}
            </Card>
          )}
        </>
      )}

      <Dialog
        open={validationDialogOpen}
        onClose={handleCloseValidationDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>CSV Validation Results</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          {uploadError && (
            <Typography color="error" gutterBottom>
              {uploadError}
            </Typography>
          )}
          {validationResult && (
            <>
              <Typography variant="body1" gutterBottom>
                Validated {validationResult.totalRows} row(s).
              </Typography>

              {hasErrors && (
                <>
                  <Typography variant="h5" color="error" sx={{ mt: 2 }} gutterBottom>
                    Errors ({allErrors.length}):
                  </Typography>
                  <Typography variant="body2" color="error" gutterBottom>
                    Fix all the errors below and re-upload the corrected file.
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Event Title</TableCell>
                        <TableCell>Column</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...allErrors]
                        .sort((a, b) => a.row - b.row)
                        .map((err) => (
                          <TableRow
                            key={`err-${err.row}-${err.column}-${err.message.slice(0, 20)}`}
                          >
                            <TableCell>{err.row}</TableCell>
                            <TableCell>{err.eventTitle ?? "-"}</TableCell>
                            <TableCell>{err.column}</TableCell>
                            <TableCell>{err.message}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </>
              )}

              {!hasErrors && (
                <Typography color="success.main" sx={{ mt: 2 }}>
                  No errors found. You can proceed with creating the events.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseValidationDialog}>{hasErrors ? "Close" : "Cancel"}</Button>
          {!hasErrors && validationResult && (
            <Button
              onClick={handleProceedWithUpload}
              variant="contained"
              color="primary"
              disabled={uploading}
              loading={uploading}
            >
              Proceed with Upload
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={resultsDialogOpen}
        onClose={handleCloseResultsDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Upload Events Results</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          {uploadError && (
            <Typography color="error" gutterBottom>
              {uploadError}
            </Typography>
          )}
          {uploadResult && (
            <>
              {uploadResult.createdEvents.length > 0 && (
                <>
                  <Typography variant="h5" color="success.main" gutterBottom>
                    Created Events ({uploadResult.createdCount}):
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Event Title</TableCell>
                        <TableCell>Event ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...uploadResult.createdEvents]
                        .sort((a, b) => a.row - b.row)
                        .map((evt) => (
                          <TableRow key={evt.id}>
                            <TableCell>{evt.row}</TableCell>
                            <TableCell>{evt.title ?? "-"}</TableCell>
                            <TableCell>
                              <Link
                                href={`${import.meta.env.VITE_APP_URL}/event/${evt.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {evt.id}
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </>
              )}

              {uploadResult.validationErrors.length > 0 && (
                <>
                  <Typography variant="h5" color="error" sx={{ mt: 2 }} gutterBottom>
                    Errors ({uploadResult.validationErrors.length}):
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Event Title</TableCell>
                        <TableCell>Column</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...uploadResult.validationErrors]
                        .sort((a, b) => a.row - b.row)
                        .map((err) => (
                          <TableRow key={`err-${err.row}`}>
                            <TableCell>{err.row}</TableCell>
                            <TableCell>{err.eventTitle ?? "-"}</TableCell>
                            <TableCell>{err.column}</TableCell>
                            <TableCell>{err.message}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </>
              )}

              {uploadResult.inviteWarnings.length > 0 && (
                <>
                  <Typography variant="h5" color="warning.main" sx={{ mt: 2 }} gutterBottom>
                    Invite Warnings ({uploadResult.inviteWarnings.length}):
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Event Title</TableCell>
                        <TableCell>Column</TableCell>
                        <TableCell>Warning</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...uploadResult.inviteWarnings]
                        .sort((a, b) => a.row - b.row)
                        .map((warn) => (
                          <TableRow key={`warn-${warn.row}`}>
                            <TableCell>{warn.row}</TableCell>
                            <TableCell>{warn.eventTitle ?? "-"}</TableCell>
                            <TableCell>{warn.column}</TableCell>
                            <TableCell>{warn.message}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResultsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventsPage;
